import { CALL_API } from 'redux-api-middleware';
import {
  keyBy,
  mapValues,
  range,
  every,
  union,
  without,
  get,
  omit,
  isEmpty
} from 'lodash';
import createCachedSelector from 're-reselect';

import { guid } from 'helpers/misc';
import { commonHeaders } from 'helpers/api';
import { reduceReducers, createReducer } from 'helpers/redux';
import paginate, { makeSelectors } from 'helpers/redux/paginate';
import { getConfig } from 'modules/config';

export const namespace = 'engine-tasks';

export const LIST = 'vtn/engine-tasks/LIST_TASKS';
export const LIST_SUCCESS = 'vtn/engine-tasks/LIST_TASKS_SUCCESS';
export const LIST_FAILURE = 'vtn/engine-tasks/LIST_TASKS_FAILURE';

export const FETCH_TASK_LOG = 'vtn/engine-tasks/FETCH_TASK_LOG';
export const FETCH_TASK_LOG_SUCCESS = 'vtn/engine-tasks/FETCH_TASK_LOG_SUCCESS';
export const FETCH_TASK_LOG_FAILURE = 'vtn/engine-tasks/FETCH_TASK_LOG_FAILURE';

export const FETCH_TASK = 'vtn/engine-tasks/FETCH_TASK';
export const FETCH_TASK_SUCCESS = 'vtn/engine-tasks/FETCH_TASK_SUCCESS';
export const FETCH_TASK_FAILURE = 'vtn/engine-tasks/FETCH_TASK_FAILURE';

export const FETCH_TASK_ASSETS = 'vtn/engine-tasks/FETCH_TASK_ASSETS';
export const FETCH_TASK_ASSETS_SUCCESS =
  'vtn/engine-tasks/FETCH_TASK_ASSETS_SUCCESS';
export const FETCH_TASK_ASSETS_FAILURE =
  'vtn/engine-tasks/FETCH_TASK_ASSETS_FAILURE';

export const LIST_TASK_METRICS = 'vtn/engine-tasks/LIST_TASK_METRICS';
export const LIST_TASK_METRICS_SUCCESS =
  'vtn/engine-tasks/LIST_TASK_METRICS_SUCCESS';
export const LIST_TASK_METRICS_FAILURE =
  'vtn/engine-tasks/LIST_TASK_METRICS_FAILURE';

export const SET_STATUS_FILTER = 'vtn/engine-tasks/SET_STATUS_FILTER';
export const SET_DATE_INTERVAL_FILTER =
  'vtn/engine-tasks/SET_DATE_INTERVAL_FILTER';

export const CREATE_SANDBOX_TASK = 'vtn/engine-tasks/CREATE_SANDBOX_TASK';
export const CREATE_SANDBOX_TASK_SUCCESS =
  'vtn/engine-tasks/CREATE_SANDBOX_TASK_SUCCESS';
export const CREATE_SANDBOX_TASK_FAILURE =
  'vtn/engine-tasks/CREATE_SANDBOX_TASK_FAILURE';

export const TOGGLE_TEST_TASK_MODAL = 'vtn/engine-tasks/TOGGLE_TEST_TASK_MODAL';

function keyByString(engineId, statusFilter, dateIntervalFilter) {
  return `${engineId}:${statusFilter}:${dateIntervalFilter}`;
}

const defaultState = {
  statusFilter: null,
  dateIntervalFilter: null,
  tasksById: {},
  metricsByEngineId: {},
  enginesLoadingMetrics: [],
  taskLogsByTaskId: {},
  taskAssetsByTaskId: {},
  loadingTaskLogs: [],
  failedLoadingTaskLogs: [],
  loadingTaskAssets: [],
  failedLoadingTaskAssets: [],
  loadingSandboxTask: false,
  loadingTasks: [],
  failedLoadingTasks: []
};

const defaultMetrics = {
  queuedCount: 0,
  runningCount: 0,
  completedCount: 0,
  failedCount: 0,
  cancelledCount: 0
};

export const statuses = [
  'complete',
  'running',
  'failed',
  'pending',
  'queued',
  'unknown'
];

const reducer = reduceReducers(
  paginate({
    types: [LIST, LIST_SUCCESS, LIST_FAILURE],
    invalidateOnTypes: [
      SET_STATUS_FILTER,
      SET_DATE_INTERVAL_FILTER,
      CREATE_SANDBOX_TASK_SUCCESS
    ],
    idAttribute: 'id',
    keyBy: (localState = {}, actionMeta = {}) => {
      return keyByString(
        actionMeta.engineId,
        localState.statusFilter,
        localState.dateIntervalFilter
      );
    },
    selectResponseResults: action => {
      return get(action.payload, 'data.engine.tasks.records', []);
    },
    selectResponseTotal: action => {
      return get(action.payload, 'data.engine.totalTasks.count', 0);
    }
  }),
  createReducer(defaultState, {
    [SET_STATUS_FILTER](state, action) {
      return {
        ...state,
        statusFilter: action.payload
      };
    },
    [SET_DATE_INTERVAL_FILTER](state, action) {
      return {
        ...state,
        dateIntervalFilter: action.payload
      };
    },
    [LIST_SUCCESS](state, action) {
      return {
        ...state,
        tasksById: {
          ...state.tasksById,
          ...keyBy(get(action.payload, 'data.engine.tasks.records', []), 'id')
        }
      };
    },
    [LIST_TASK_METRICS](state, action) {
      return {
        ...state,
        enginesLoadingMetrics: union(state.enginesLoadingMetrics, [
          action.meta.engineId
        ])
      };
    },
    [LIST_TASK_METRICS_SUCCESS](state, action) {
      return {
        ...state,
        metricsByEngineId: {
          [action.meta.engineId]: {
            // substitute default values for "null" results
            ...mapValues(
              action.payload,
              (val, metric) =>
                val === null
                  ? defaultMetrics[metric]
                  : get(val, 'engine.taskMetrics', defaultMetrics[metric])
            )
          }
        },
        enginesLoadingMetrics: without(
          state.enginesLoadingMetrics,
          action.meta.engineId
        )
      };
    },
    [LIST_TASK_METRICS_FAILURE](state, action) {
      // todo: error state
      return {
        ...state,
        metricsByEngineId: {
          [action.meta.engineId]: defaultMetrics
        },
        enginesLoadingMetrics: without(
          state.enginesLoadingMetrics,
          action.meta.engineId
        )
      };
    },
    [FETCH_TASK_LOG](state, action) {
      return {
        ...state,
        // clear old data if it exists
        taskLogsByTaskId: {
          ...state.taskLogsByTaskId,
          [action.meta.taskId]: undefined
        },
        // add to loading, remove from failure
        loadingTaskLogs: union(state.loadingTaskLogs, [action.meta.taskId]),
        failedLoadingTaskLogs: without(
          state.failedLoadingTaskLogs,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_LOG_SUCCESS](state, action) {
      return {
        ...state,
        taskLogsByTaskId: {
          ...state.taskLogsByTaskId,
          [action.meta.taskId]: action.payload
        },
        // remove from loading and failure
        loadingTaskLogs: without(state.loadingTaskLogs, action.meta.taskId),
        failedLoadingTaskLogs: without(
          state.failedLoadingTaskLogs,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_LOG_FAILURE](state, action) {
      return {
        ...state,
        // remove from loading, add to failure
        loadingTaskLogs: without(state.loadingTaskLogs, action.meta.taskId),
        failedLoadingTaskLogs: union(state.failedLoadingTaskLogs, [
          action.meta.taskId
        ])
      };
    },
    [FETCH_TASK](state, action) {
      return {
        ...state,
        loadingTasks: union(state.loadingTasks, [action.meta.taskId]),
        failedLoadingTasks: without(
          state.failedLoadingTasks,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_SUCCESS](state, action) {
      if (
        !isEmpty(action.payload.errors) &&
        get(action.payload.errors[0], 'data.objectType') !== 'Build' // test tasks will throw false positives
      ) {
        return this[FETCH_TASK_FAILURE](state, action);
      }

      return {
        ...state,
        tasksById: {
          ...state.tasksById,
          [action.meta.taskId]: {
            ...state.tasksById[action.meta.taskId],
            ...get(action.payload, 'data.task')
          }
        },
        loadingTasks: without(state.loadingTasks, action.meta.taskId),
        failedLoadingTasks: without(
          state.failedLoadingTasks,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_FAILURE](state, action) {
      return {
        ...state,
        loadingTasks: without(state.loadingTasks, action.meta.taskId),
        failedLoadingTasks: union(state.failedLoadingTasks, [
          action.meta.taskId
        ])
      };
    },
    [FETCH_TASK_ASSETS](state, action) {
      return {
        ...state,
        // clear old data if it exists
        taskAssetsByTaskId: omit(state.taskAssetsByTaskId, action.meta.taskId),
        // add to loading, remove from failure
        loadingTaskAssets: union(state.loadingTaskAssets, [action.meta.taskId]),
        failedLoadingTaskAssets: without(
          state.failedLoadingTaskAssets,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_ASSETS_SUCCESS](state, action) {
      if (action.payload.errors && action.payload.errors.length) {
        return this[FETCH_TASK_ASSETS_FAILURE](state, action);
      }

      return {
        ...state,
        taskAssetsByTaskId: {
          ...state.taskAssetsByTaskId,
          [action.meta.taskId]: get(
            action.payload,
            'data.temporalDataObject.assets.records'
          )
        },
        // remove from loading and failure
        loadingTaskAssets: without(state.loadingTaskAssets, action.meta.taskId),
        failedLoadingTaskAssets: without(
          state.failedLoadingTaskAssets,
          action.meta.taskId
        )
      };
    },
    [FETCH_TASK_ASSETS_FAILURE](state, action) {
      return {
        ...state,
        // remove from loading, add to failure
        loadingTaskAssets: without(state.loadingTaskAssets, action.meta.taskId),
        failedLoadingTaskAssets: union(state.failedLoadingTaskAssets, [
          action.meta.taskId
        ])
      };
    },
    [CREATE_SANDBOX_TASK](state, action) {
      const requestSuccessState = {
        ...state,
        loadingSandboxTask: true
      };
      return action.error // handle requestError ie. offline
        ? this[CREATE_SANDBOX_TASK_FAILURE](state, action)
        : requestSuccessState;
    },
    [CREATE_SANDBOX_TASK_SUCCESS](state, action) {
      if (
        !isEmpty(action.payload.errors) &&
        get(action.payload.errors[0], 'data.objectType') !== 'Build' // test tasks will throw false positives
      ) {
        return this[CREATE_SANDBOX_TASK_FAILURE](state, action);
      }

      return {
        ...state,
        loadingSandboxTask: false
      };
    },
    [CREATE_SANDBOX_TASK_FAILURE](state, action) {
      return {
        ...state,
        loadingSandboxTask: false
      };
    }
  })
);

export default reducer;

function local(state) {
  return state[namespace];
}

// Tasks now include logs and job data
export function listTasks(
  engineId,
  { offset = 0, limit = 10 } = {},
  { force } = {}
) {
  const requestId = guid();
  const query = `
    ${engineTaskFields}
    query Engines(
      $engineId: ID!
      $dateTime: [TaskDateTimeFilter]
      $status: [String]
      $offset: Int
      $limit: Int
    ) {
      engine(id: $engineId) {
        totalTasks: tasks(
          dateTimeFilter: $dateTime
          status: $status
          limit: 99999
        ) {
          count
        }
        tasks(
          dateTimeFilter: $dateTime
          status: $status
          offset: $offset
          limit: $limit
        ) {
          count
          records {
            ...taskFields
          }
        }
      }
    }`;

  return {
    [CALL_API]: {
      types: [LIST, LIST_SUCCESS, LIST_FAILURE].map(type => ({
        type,
        meta: {
          engineId,
          requestId,
          offset,
          limit
        }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      body: state => {
        const variables = {
          engineId,
          offset,
          limit
        };

        const dateIntervalFilter = local(state).dateIntervalFilter;
        if (dateIntervalFilter) {
          variables.dateTime = [
            {
              toDateTime: dateIntervalFilter.end.toISOString(),
              fromDateTime: dateIntervalFilter.start.toISOString(),
              field: 'createdDateTime'
            }
          ];
        }

        const statusFilter = local(state).statusFilter;
        if (statusFilter) {
          variables.status = [statusFilter];
        }
        return JSON.stringify({
          query,
          variables
        });
      },
      bailout: state => {
        if (force) {
          return false;
        }

        const tasks = getPagedTasksForEngine(state, engineId);
        const needStart = offset;
        const needEnd = offset + limit - 1;

        // bail if all needed tasks exist
        return every(range(needStart, needEnd + 1), index => {
          return !!tasks[index];
        });
      },
      method: 'POST',
      headers: commonHeaders
    }
  };
}

export function createSandboxTask(engineId, formValues) {
  const query = `
    ${engineTaskFields}
    mutation createJob ($input: CreateJob) {
      createJob (input: $input) {
        tasks {
          records {
            ...taskFields
          }
        }
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [
        {
          type: CREATE_SANDBOX_TASK,
          meta: {
            engineId
          }
        },
        {
          type: CREATE_SANDBOX_TASK_SUCCESS,
          payload: (action, state, res) => {
            return new Promise((resolve, reject) => {
              res
                .json()
                .then(result =>
                  setTimeout(() => {
                    resolve(result);
                  }, 1500)
                ) // delay is needed here due to API returning 200 before the task is available
                .catch(e => reject(e));
            });
          },
          meta: {
            engineId
          }
        },
        {
          type: CREATE_SANDBOX_TASK_FAILURE,
          meta: {
            engineId
          }
        }
      ],
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input: constructTaskPayload(engineId, formValues)
        }
      }),
      headers: commonHeaders
    }
  };
}

export function fetchTask(taskId) {
  const query = `
    ${engineTaskFields}
    query FetchTask($taskId: ID!) {
      task(id: $taskId) {
        ...taskFields
      }
    }
  `;
  return {
    [CALL_API]: {
      types: [FETCH_TASK, FETCH_TASK_SUCCESS, FETCH_TASK_FAILURE].map(type => ({
        type,
        meta: { taskId }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          taskId
        }
      }),
      headers: commonHeaders,
      bailout: state => taskJobIsLoading(state, taskId)
    }
  };
}

export function listMetrics(engineId) {
  const query = `
    query FetchEngineMetrics($engineId: ID!, $fromDateTime: DateTime, $toDateTime: DateTime) {
      engine(
        id: $engineId
      ) {
        taskMetrics(
          fromDateTime: $fromDateTime
          toDateTime: $toDateTime
        ) {
          cancelledCount
          completedCount
          failedCount
          pendingCount
          queuedCount
          runningCount
        }
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [
        LIST_TASK_METRICS,
        LIST_TASK_METRICS_SUCCESS,
        LIST_TASK_METRICS_FAILURE
      ].map(type => ({
        type,
        meta: {
          engineId
        }
      })),
      endpoint: state => {
        return getConfig(state).services.graphqlUri;
      },
      method: 'POST',
      body: state => {
        let fromDateTime = null;
        let toDateTime = null;
        const dateIntervalFilter = local(state).dateIntervalFilter;

        if (dateIntervalFilter) {
          fromDateTime = dateIntervalFilter.start.toISOString();
          toDateTime = dateIntervalFilter.end.toISOString();
        }

        return JSON.stringify({
          query,
          variables: {
            engineId,
            fromDateTime,
            toDateTime
          }
        });
      },
      headers: commonHeaders
    }
  };
}

export function setStatusFilter(status) {
  return {
    type: SET_STATUS_FILTER,
    payload: status
  };
}

export function setDateIntervalFilter(interval) {
  return {
    type: SET_DATE_INTERVAL_FILTER,
    payload: interval
  };
}

export function fetchTaskAssets(tdoId, taskId) {
  const query = `
    query fetchTaskAssets($tdoId: ID!, $taskId: ID!) {
      temporalDataObject(id: $tdoId) {
        assets(sourceTaskId: $taskId) {
          records {
            id
            type
            contentType
            signedUri
          }
        }
      }
    }`;
  return {
    [CALL_API]: {
      types: [
        FETCH_TASK_ASSETS,
        FETCH_TASK_ASSETS_SUCCESS,
        FETCH_TASK_ASSETS_FAILURE
      ].map(type => ({
        type,
        meta: {
          taskId
        }
      })),
      endpoint: state => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          tdoId,
          taskId
        }
      }),
      headers: commonHeaders,
      bailout: state => taskAssetsAreLoading(state, taskId)
    }
  };
}

export function getStatusFilter(state) {
  return local(state).statusFilter;
}

export function getDateIntervalFilter(state) {
  return local(state).dateIntervalFilter;
}

export function getMetrics(state, engineId) {
  return get(
    local(state),
    ['metricsByEngineId', engineId, 'data'],
    defaultMetrics
  );
}

export function isLoadingSandboxTask(state) {
  return local(state).loadingSandboxTask;
}

export function engineIsLoadingMetrics(state, engineId) {
  return local(state).enginesLoadingMetrics.includes(engineId);
}

export function getTaskAssets(state, taskId) {
  return local(state).taskAssetsByTaskId[taskId];
}

export function taskAssetsAreLoading(state, taskId) {
  return local(state).loadingTaskAssets.includes(taskId);
}

export function taskAssetsFailedToLoad(state, taskId) {
  return local(state).failedLoadingTaskAssets.includes(taskId);
}

export function getTaskLog(state, taskId) {
  return local(state).taskLogsByTaskId[taskId];
}

export function taskLogIsLoading(state, taskId) {
  return local(state).loadingTaskLogs.includes(taskId);
}

export function taskLogFailedToLoad(state, taskId) {
  return local(state).failedLoadingTaskLogs.includes(taskId);
}

export function taskJobIsLoading(state, taskId) {
  return local(state).loadingTasks.includes(taskId);
}

export function taskJobFailedToLoad(state, taskId) {
  return local(state).failedLoadingTasks.includes(taskId);
}

export const getPagedTasksForEngine = createCachedSelector(
  (state, engineId) => {
    const statusFilter = getStatusFilter(state);
    const dateIntervalFilter = getDateIntervalFilter(state);
    const key = keyByString(engineId, statusFilter, dateIntervalFilter);

    return makeSelectors(key).selectPaginationOrder(local(state));
  },
  (state, engineId) => local(state).tasksById,
  (taskOrder, tasksById, ...args) => mapValues(taskOrder, id => tasksById[id])
)((state, engineId) => engineId);

export function getListTasksStatus(state, engineId) {
  const statusFilter = getStatusFilter(state);
  const dateIntervalFilter = getDateIntervalFilter(state);
  const key = keyByString(engineId, statusFilter, dateIntervalFilter);

  return makeSelectors(key).selectFetchingStatus(local(state));
}

export function getLoadingTasks(state, engineId) {
  const statusFilter = getStatusFilter(state);
  const dateIntervalFilter = getDateIntervalFilter(state);
  const key = keyByString(engineId, statusFilter, dateIntervalFilter);

  return makeSelectors(key).selectFetchingEntities(local(state));
}

export function getFailedLoadingTasks(state, engineId) {
  const statusFilter = getStatusFilter(state);
  const dateIntervalFilter = getDateIntervalFilter(state);
  const key = keyByString(engineId, statusFilter, dateIntervalFilter);

  return makeSelectors(key).selectFailedFetchingEntities(local(state));
}

export function getTotalTasks(state, engineId) {
  const statusFilter = getStatusFilter(state);
  const dateIntervalFilter = getDateIntervalFilter(state);
  const key = keyByString(engineId, statusFilter, dateIntervalFilter);

  return makeSelectors(key).selectTotalEntities(local(state));
}

function constructTaskPayload(
  engineId,
  { buildId, selectedFile, parameters, libraryIds }
) {
  let tasks = [];

  const isTestTask = buildId === 'local';

  if (libraryIds) {
    libraryIds.forEach(libraryId =>
      tasks.push({
        engineId,
        buildId: isTestTask ? null : buildId,
        testTask: isTestTask,
        payload: {
          libraryId,
          ...parameters
        }
      })
    );
  } else {
    tasks.push({
      engineId,
      buildId: isTestTask ? null : buildId,
      testTask: isTestTask,
      payload: parameters
    });
  }

  return {
    tasks: [
      {
        engineId: 'download-file',
        payload: {
          startDateTime: Math.round(new Date().getTime() / 1000), // current timestamp
          fileUri: selectedFile.url,
          metadata: {
            'veritone-program': {
              programImage: '',
              programLiveImage: '',
              programId: '-1',
              programName: 'Upload'
            },
            tags: [],
            source: {
              ingestionType: 'upload'
            },
            'veritone-file': {
              filename: selectedFile.name,
              size: selectedFile.size,
              mimetype: selectedFile.type
            }
          }
        }
      },
      {
        engineId: 'insert-into-index'
      },
      {
        engineId: 'thumbnail-generator',
        payload: {}
      },
      ...tasks
    ]
  };
}

const engineTaskFields = `
fragment taskFields on Task {
  applicationId
  createdDateTime
  engineId
  build {
    version
  }
  isClone
  jobId
  modifiedDateTime
  parentTaskId
  payload
  id
  output
  taskPayload
  targetId
  status
  output
  testTask
  job {
    applicationId
    name
    status
    targetId
    createdBy
    templateId
    sourceAssetId
    createdDateTime
    modifiedDateTime
    clusterId
    description
    id
  }
  log {
    jsondata
    uri
    text
  }
  startedDateTime
  completedDateTime
  mediaLengthSec
  queuedDateTime
}
`;



// WEBPACK FOOTER //
// ./src/client/modules/engine-tasks/index.js